import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Box
} from '@material-ui/core';

import { formatDate } from '../../common/formatter';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import UserLink from '../users/components/UserLink';
import DocumentTableHead from './components/DocumentTableHead';
import { FileDto } from './Document.interface';
import FileIcon from './components/FileIcon';
import environment from '../../environments';

const StyledTable = styled(Table)`
  th,
  td {
    .align-icon {
      display: flex;
      align-items: center;
      .file-name {
        margin-left: 5px;
      }
    }
    &:first-child {
      padding-left: 10px;
    }
    &:last-child {
      padding-right: 10px;
    }
  }
  .file-icon {
    width: 20px;
  }
`;

export interface DocumentListProps {
  files: FileDto[];
  isLoading: boolean;
  selectedIDs: number[];
  onRowClick?: (file: FileDto) => void;
  onRowDoubleClick?: (file: FileDto) => void;
  onSelectionChange: (selection: number[]) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  files,
  isLoading,
  onRowClick,
  onRowDoubleClick,
  selectedIDs,
  onSelectionChange
}) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof FileDto>('name');
  const rows: any = files;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof FileDto
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const checkboxAllProps = {
    indeterminate:
      selectedIDs.length > 0 && selectedIDs.length !== files.length,
    checked: selectedIDs.length > 0 && selectedIDs.length === files.length
  };

  const onSelectToggle = (selectedID: number, checked: boolean) => {
    // don't directly edit 'selectedFiles'
    const selection: number[] = [...selectedIDs];
    const existingIndex: number = selection.findIndex(id => id === selectedID);
    const exists: boolean = existingIndex > -1;

    if (checked) {
      !exists && selection.push(selectedID);
    } else {
      exists && selection.splice(existingIndex, 1);
    }

    onSelectionChange(selection);
  };

  const onSelectAllToggle = (checked: boolean) => {
    const partial: boolean = checked && !checkboxAllProps.checked;
    const selection: number[] = partial ? files.map(file => file.id) : [];
    onSelectionChange(selection);
  };

  return (
    <TableContainer>
      <StyledTable stickyHeader>
        <DocumentTableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          checkboxAllProps={checkboxAllProps}
          onSelectAllToggle={onSelectAllToggle}
        />
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={7}>
                <Box
                  height={300}
                  width='100%'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CircularProgress />
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            Array.isArray(rows) &&
            stableSort(rows, getComparator(order, orderBy)).map(file => {
              const isSelected: boolean = selectedIDs.includes(file.id);
              return (
                <DocumentListItem
                  key={file.id}
                  file={file}
                  isSelected={isSelected}
                  onRowClick={() => onRowClick?.(file)}
                  onRowDoubleClick={() => onRowDoubleClick?.(file)}
                  onSelectToggle={(checked: boolean) => {
                    onSelectToggle?.(file.id, checked);
                  }}
                />
              );
            })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | any },
  b: { [key in Key]: number | string | any }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

export default DocumentList;

export interface DocumentListItemProps {
  file: FileDto;
  isSelected?: boolean;
  onRowClick?: () => void;
  onRowDoubleClick?: () => void;
  onSelectToggle?: (checked: boolean) => void;
}

export const DocumentListItem: React.FC<DocumentListItemProps> = (
  props: DocumentListItemProps
) => {
  const {
    file,
    isSelected = false,
    onRowClick,
    onRowDoubleClick,
    onSelectToggle
  } = props;
  const versionLog = file.task?.versionLog;
  const version =
    versionLog?.version || file.task?.version || file.version || 1;
  return (
    <TableRow
      hover
      selected={isSelected}
      onClick={onRowClick}
      onDoubleClick={onRowDoubleClick}
    >
      <TableCell padding='checkbox'>
        <Checkbox
          color='primary'
          checked={isSelected}
          onClick={event => event.stopPropagation()}
          onChange={(_, checked: boolean) => onSelectToggle?.(checked)}
        />
      </TableCell>
      <TableCell>
        <div className='align-icon'>
          <FileIcon
            isFolder={file.isFolder}
            path={file.path}
            extension={file.extension}
            className='file-icon'
          />
          <span className='file-name'>{file.name}</span>
        </div>
      </TableCell>

      {!environment.fileConfig?.hideTaskKey && (
        <TableCell>
          {file.isFolder ? '' : file.task?.key || `DOC-${file.id}`}
        </TableCell>
      )}
      {!environment.fileConfig?.hideInternalDocumentId && (
        <TableCell>{file.isFolder ? '' : file.internalDocumentId}</TableCell>
      )}

      {!environment.fileConfig?.hideVersion && <TableCell>{version}</TableCell>}
      {!environment.fileConfig?.hideRev && <TableCell>{file.rev}</TableCell>}

      <TableCell>
        <UserLink type='name' user={file.modifiedBy} />
      </TableCell>
      <TableCell>{formatDate(file.modified)}</TableCell>
      <TableCell>
        {/* DocViewer will view status from last approved version log. */}
        {file?.task && (
          <TaskStatusLabel task={file.task} status={versionLog?.status} />
        )}
      </TableCell>
    </TableRow>
  );
};
